/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import useMounted from '../../../shared/useMount/useMount';
import {
  formattedDate,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import IEnergyManagementProps, {
  IAny,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import CarbonScopeOneDetailWidget from '../CarbonScopeOneDetailWidget/CarbonScopeOneDetailWidget';
import CarbonScopeOneSavingsByMeterComponent from '../CarbonScopeOneSavingsByMeterWidget/CarbonScopeOneSavingsByMeterComponent';
import { UtilityType } from '../../../shared/Utils/Constants';
import { getCarbonVDEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ApiContext } from '../../../../context/ApiContext';
import { Button } from '@scuf/common';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';
import ScopeOneErrorModel from '../../../shared/ScopeOneErrorModel/ScopeOneErrorModel';
import { EmaasSubscriptionType } from '../../../shared/Enums';
import CarbonScopeOneDetailWidgetSeparateAPI from '../CarbonScopeOneDetailWidget/CarbonScopeOneDetailWidgetSeparateAPI';
import CarbonScopeOneSavingsByMeterComponentSeparate from '../CarbonScopeOneSavingsByMeterWidget/CarbonScopeOneSavingsByMeterComponentSeparate';
const getAdjustedEndDate = (userEndDate: string | Date) => {
  // Parse the user's end date
  const userDate = new Date(userEndDate);
  const currentDate = new Date();

  // Check if the user's end date month and year are the same as the current month and year
  if (
    userDate.getMonth() === currentDate.getMonth() &&
    userDate.getFullYear() === currentDate.getFullYear()
  ) {
    // Subtract one month from the user's end date
    userDate.setMonth(userDate.getMonth() - 1);
  }

  return userDate.toISOString();
};
const getConnectorConfigCarbon = (
  toggleFeatureScopeOne: boolean | undefined,
  requestContext: IRequestContext,
  isSitePremium: boolean | undefined
) => {
  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!,
      requestContext.context.dataSource!
    )
  };
  if (toggleFeatureScopeOne) {
    return {
      name: isSitePremium ? 'carbon-site-summary' : 'site-carbon-new',
      options: {
        ...params,
        scopeType: 'scope1',
        dataSource: isSitePremium ? requestContext.context.dataSource || '' : ''
      }
    };
  } else {
    return {
      name: isSitePremium ? 'carbon-site-summary' : 'site-carbon-new-v1',
      options: {
        ...params,
        allDetails: 'true',
        utilityType: 'gas',
        dataSource: isSitePremium ? requestContext.context.dataSource || '' : ''
      }
    };
  }
};

const getConnectorConfigGraph = (
  toggleFeatureScopeOne: boolean | undefined,
  requestContext: IRequestContext
) => {
  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!,
      requestContext.context.dataSource!
    )
  };
  if (toggleFeatureScopeOne) {
    return {
      name: 'site-carbon-new-graph',
      options: {
        ...params,
        scopeType: 'scope1',
        dataSource: requestContext.context.dataSource || ''
      }
    };
  } else {
    return {
      name: 'carbon-site-Graph',
      options: {
        ...params,
        allDetails: 'true',
        utilityType: 'gas',
        dataSource: requestContext.context.dataSource || ''
      }
    };
  }
};

const getConnectorConfigTable = (
  toggleFeatureScopeOne: boolean | undefined,
  requestContext: IRequestContext
) => {
  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!,
      requestContext.context.dataSource!
    )
  };
  if (toggleFeatureScopeOne) {
    return {
      name: 'site-carbon-new-table',
      options: {
        ...params,
        scopeType: 'scope1',
        groupType: 'scopetype',
        dataSource: requestContext.context.dataSource || ''
      }
    };
  } else {
    return {
      name: 'carbon-site-table',
      options: {
        ...params,
        allDetails: 'true',
        scopeType: 'scope1',
        groupType: 'scopetype',
        dataSource: requestContext.context.dataSource || ''
      }
    };
  }
};

const CarbonScopeOneDetailsComponent = (props: IEnergyManagementProps) => {
  const { bearerToken, requestContext } = props;
  const [popUpDisplay, setpopUpDisplay] = useState(false);
  const emaasContext = useContext(ApiContext);
  const [noDataCount, setnoDataCount] = useState<number>(0);
  const { toggleFeatureScopeOne, toggleSeparateApi } = emaasContext!;
  const dataSource = requestContext.context.dataSource || '';
  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION && toggleSeparateApi;

  const isMounted = useMounted();

  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!
    )
  };

  const connectorConfig = getConnectorConfigCarbon(
    toggleFeatureScopeOne,
    requestContext,
    isSitePremium
  );

  const connectorConfigForGraph = getConnectorConfigGraph(
    toggleFeatureScopeOne,
    requestContext
  );

  const connectorConfigTable = getConnectorConfigTable(
    toggleFeatureScopeOne,
    requestContext
  );

  const connectorConfigMissigData = {
    name: 'get-missing-data-details-forPeriod',
    options: {
      ...params
    }
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const [dataConnectorForGraph] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForGraph,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  const [dataConnectorTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigTable,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  const [dataConnectorMissingData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigMissigData,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
      dataConnectorMissingData?.actions?.getAll();
      if (isSitePremium) {
        dataConnectorForGraph?.actions?.getAll();
        dataConnectorTable?.actions?.getAll();
        // dataConnectorMissingData?.actions?.getAll();
      }
    }
  }, [requestContext, dataSource]);

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
      dataConnectorMissingData?.actions?.getAll();
      if (isSitePremium) {
        dataConnectorForGraph?.actions?.getAll();
        dataConnectorTable?.actions?.getAll();
        // dataConnectorMissingData?.actions?.getAll();
      }
    }
  }, [popUpDisplay]);

  const {
    all: allMissingData,
    loading: loadingMissingData,
    error: errorMissingData,
    status: statusMissingData
  } = dataConnectorMissingData.data;

  const MissingDataCondition = allMissingData as IAny;
  const isMissingDataAvailable = MissingDataCondition ? true : false;

  useEffect(() => {
    if (
      (isMissingDataAvailable || errorMissingData || statusMissingData) &&
      !loadingMissingData
    ) {
      setnoDataCount(MissingDataCondition.totalAlerts);
    }
  }, [
    popUpDisplay,
    isMissingDataAvailable,
    loadingMissingData,
    statusMissingData
  ]);

  const UtilityBillType = UtilityType.Gas;

  const modalVal = {
    bearerToken: bearerToken,
    requestContext: requestContext,
    showErrorModal: popUpDisplay,
    setpopUpDisplay
  };

  const showErrorModalPopup = () => setpopUpDisplay(true);

  return (
    <div className="total-emissions-scope-one-details">
      {toggleFeatureScopeOne && noDataCount > 0 ? (
        <div className="errorSenarioScope1">
          <img
            className="alarm-meter-icon"
            src={require('../../../../images/information.svg')}
          />
          <Button
            type="link"
            content="Scope 1 data missing"
            onClick={showErrorModalPopup}
          />
        </div>
      ) : (
        ''
      )}
      <ScopeOneErrorModel modalVal={modalVal} />
      {isSitePremium ? (
        <CarbonScopeOneDetailWidgetSeparateAPI
          isSite
          dataConnector={dataConnector}
          dataConnectorForGraph={dataConnectorForGraph}
          dataConnectorForTable={dataConnectorTable}
          requestContext={requestContext}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
        />
      ) : (
        <CarbonScopeOneDetailWidget
          isSite
          dataConnector={dataConnector}
          bearerToken={bearerToken}
          requestContext={requestContext}
        />
      )}{' '}
      {isSitePremium ? (
        <CarbonScopeOneSavingsByMeterComponentSeparate
          toggleFeatureScopeOne={toggleFeatureScopeOne}
          dataConnector={dataConnectorTable}
          UtilityBillType={UtilityBillType}
        />
      ) : (
        <CarbonScopeOneSavingsByMeterComponent
          dataConnector={dataConnector}
          UtilityBillType={UtilityBillType}
        />
      )}
    </div>
  );
};
export default CarbonScopeOneDetailsComponent;
